import { Configuration, SuppliersApi } from '@/api';
import CustomToast from '@/components/CustomToast';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import CustomerForm from '@/modules/supplier/components/CustomerForm';
import { CustomerAddOrUpdate } from '@/modules/supplier/models/Customer';
import { CustomerStoreListPath } from '@/modules/supplier/paths';
import { UsersIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const CustomerAdd = () => {
  const queryClient = useQueryClient();

  const invalidateStoreQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && key[0] === 'retailers';
      },
    });
  };

  const { currentUser } = useAuth();

  const supplierId = currentUser()?.supplierId;

  const navigate = useNavigate();

  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const addCustomerToSupplier = async (customer: CustomerAddOrUpdate, supplierId: string) => {
    if (!customer.id) return;

    await suppliersApi.addRetailersToSupplier(supplierId, [
      {
        id: customer.id,
        customerCode: customer.customerCode,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Customer added successfully!" visible={t.visible} />
    ));
  };

  const removeCustomerFromSupplier = async (customer: CustomerAddOrUpdate, supplierId: string) => {
    if (!customer.id) return;

    await suppliersApi.removeRetailersFromSupplier(supplierId, [
      {
        id: customer.id,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Customer removed successfully!" visible={t.visible} />
    ));
  };

  const handleSubmit = async (customer: CustomerAddOrUpdate) => {
    if (!supplierId) return;

    const shouldAddStore = customer.customerCode !== '';

    try {
      if (shouldAddStore) {
        await addCustomerToSupplier(customer, supplierId);
      } else {
        await removeCustomerFromSupplier(customer, supplierId);
      }

      await invalidateStoreQueries();

      const customerId = customer.customerId || customer.id;
      if (!customerId) return;
      navigate(CustomerStoreListPath.withCustomerId(customerId));
    } catch (error) {
      console.error('Error while submitting store:', error);
    }
  };

  return (
    <>
      <SettingsHeader title="Create Customer" icon={<UsersIcon className="w-6 h-6" />} />

      <CustomerForm onSubmit={handleSubmit} />
    </>
  );
};

export default CustomerAdd;
